import { useEffect, useRef } from "react";
import api from "@/lib/api";

// Schedules a browser notification shortly before the employee's individual cutoff
// if attendance is not yet marked today. Uses server time (Asia/Kolkata) as source of truth.
export default function useAttendanceReminder() {
  const timers = useRef([]);

  useEffect(() => {
    let cancelled = false;
    const clear = () => { timers.current.forEach((t) => clearTimeout(t)); timers.current = []; };

    async function schedule() {
      try {
        if (!("Notification" in window)) return;
        if (Notification.permission === "default") {
          try { await Notification.requestPermission(); } catch (e) { console.debug("Notification permission request failed", e); }
        }
        if (Notification.permission !== "granted") return;

        const [today, st] = await Promise.all([
          api.get("/attendance/today").then((r) => r.data),
          api.get("/server-time").then((r) => r.data),
        ]);
        if (cancelled) return;
        if (today.record) return; // already marked
        if (!today.cutoff_time) return;

        const serverNow = new Date(st.iso);
        const [ch, cm] = today.cutoff_time.split(":").map(Number);
        // cutoff datetime on server's calendar day
        const cutoff = new Date(serverNow);
        cutoff.setHours(ch, cm, 0, 0);

        const localNow = new Date();
        const skew = localNow.getTime() - serverNow.getTime(); // local - server

        const remindAt = cutoff.getTime() - 10 * 60 * 1000; // 10 min before cutoff
        const fireLocal = remindAt + skew;
        const delay = fireLocal - localNow.getTime();

        const notify = () => {
          try {
            new Notification("Mark your attendance", {
              body: `Attendance closes at ${today.cutoff_time}. Please mark it now.`,
              icon: "/icon-192.png",
              tag: "wp-attendance-reminder",
            });
          } catch (e) { console.debug("Attendance reminder notification failed", e); }
        };

        if (delay > 0 && delay < 12 * 60 * 60 * 1000) {
          timers.current.push(setTimeout(notify, delay));
        } else if (cutoff.getTime() + skew - localNow.getTime() > 0 && delay <= 0) {
          // within the last 10 minutes already — remind soon
          timers.current.push(setTimeout(notify, 5000));
        }
      } catch (e) {
        // reminders never block core flow, but log for debugging
        console.debug("Attendance reminder scheduling skipped", e);
      }
    }

    schedule();
    return () => { cancelled = true; clear(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
